import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * Incident Desk — tab shell.
 *
 * Pattern: `StationMatesLayout`. `AppShell` takes only `{ children }` and its sub-bar has
 * no slot, so this layout does not touch the shell.
 *
 * A group whose every tab is denied is hidden, because a heading over nothing is noise.
 * An individual tab is NOT hidden on a maybe — the server's refusal is what speaks. That
 * distinction matters most here: hiding "Report" from someone who turns out to hold
 * `incident:report` means an incident goes unreported.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

export const INCIDENT_TABS: { label: string; tabs: Tab[] }[] = [
  {
    label: 'Desk',
    tabs: [
      {
        to: '/incidents',
        label: 'Register',
        hint: 'Every incident in scope, with filters and bulk actions',
        end: true,
      },
      {
        to: '/incidents/report',
        label: 'Report',
        hint: 'The seven-step report. Resumable — the draft is a server row',
        permission: 'incident:report',
      },
    ],
  },
  {
    label: 'Read',
    tabs: [
      {
        to: '/incidents/map',
        label: 'Map',
        hint: 'Severity markers where an incident has a coordinate',
      },
      {
        to: '/incidents/analytics',
        label: 'Analytics',
        hint: 'Breakdowns, the 90-day trend, geography and response times',
        permission: 'incident:analyse',
      },
    ],
  },
]

export function IncidentsLayout() {
  const { search } = useLocation()
  const authorities = useSession((s) => s.authorities)

  const groups = INCIDENT_TABS.map((group) => ({
    ...group,
    tabs: group.tabs.filter((tab) => hasPermission(authorities, tab.permission)),
  })).filter((group) => group.tabs.length > 0)

  return (
    <div className="space-y-4">
      <nav aria-label="Incident Desk" className="axis-table-scroll border-b border-border">
        <ul className="flex items-stretch gap-4 min-w-max">
          {groups.map((group) => (
            <li key={group.label} className="flex items-stretch gap-1">
              <span
                className="self-center pr-1 text-erp-xs uppercase tracking-wide text-muted-foreground/70"
                aria-hidden
              >
                {group.label}
              </span>
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={{ pathname: tab.to, search }}
                  end={tab.end}
                  title={tab.hint}
                  className={({ isActive }) =>
                    cn(
                      'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors',
                      isActive
                        ? 'border-chocolate text-chocolate'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  )
}
